import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { settingsApi } from "../../api/settings";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import LandingSettingsDialog from "../../components/dashboard/landingEditor/LandingSettingsDialog";
import LandingSectionDialog from "../../components/dashboard/landingEditor/LandingSectionDialog";
import LandingControlCenter from "../../components/dashboard/landingEditor/LandingControlCenter";
import SiteBuilderGuide from "../../components/dashboard/landingEditor/SiteBuilderGuide";
import { BRAND, emptySettings, mergeSettings } from "../../components/dashboard/landingEditor/config";
import { settingsPayload } from "../../components/dashboard/landingEditor/editorUtils";
import { assetValueFromResponse, isManagedLandingAsset } from "../../utils/landingAssets";
import { TRIAL_LANDING_EVENT } from "../../utils/trialOnboarding";
import {
  findStepForSection,
  getSiteBuilderProgress,
} from "../../components/dashboard/landingEditor/siteBuilderModel";

const TAB_STEP_MAP = {
  0: "identity",
  1: "identity",
  2: "publish",
  3: "contact",
  4: "publish",
};

export default function ProjectSitePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [form, setForm] = React.useState(emptySettings);
  const [activeStep, setActiveStep] = React.useState("identity");
  const [loading, setLoading] = React.useState(true);
  const [savingPath, setSavingPath] = React.useState("");
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState(0);
  const [sectionOpen, setSectionOpen] = React.useState(false);
  const [sectionId, setSectionId] = React.useState("identity");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [soundEnabled, setSoundEnabled] = React.useState(() => {
    try {
      return window.localStorage.getItem("wp-landing-sound") !== "off";
    } catch {
      return true;
    }
  });
  const missionHandledRef = React.useRef("");
  const audioContextRef = React.useRef(null);
  const completionAnnouncedRef = React.useRef(false);

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject]
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const nextIncompleteStep = progress.steps.find((step) => !step.done) || null;
  const requestedMission = searchParams.get("mision") || "";
  const saving = Boolean(savingPath);

  const playTone = React.useCallback(
    (kind = "click", allowCreate = true, force = false) => {
      if ((!soundEnabled && !force) || typeof window === "undefined") return;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      try {
        if (!audioContextRef.current && allowCreate) audioContextRef.current = new AudioContext();
        const context = audioContextRef.current;
        if (!context) return;
        if (context.state === "suspended") context.resume().catch(() => {});

        const tones =
          kind === "success"
            ? [620, 820]
            : kind === "reminder"
              ? [430, 520]
              : [520];

        tones.forEach((frequency, index) => {
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          const start = context.currentTime + index * 0.1;
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(frequency, start);
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.055, start + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(start);
          oscillator.stop(start + 0.12);
        });
      } catch {
        // El audio es una ayuda opcional; nunca debe bloquear el editor.
      }
    },
    [soundEnabled]
  );

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try {
      window.localStorage.setItem("wp-landing-sound", next ? "on" : "off");
    } catch {
      // Ignorar restricciones del navegador.
    }
    if (next) {
      window.setTimeout(() => playTone("click", true, true), 0);
      setMessage("Guía sonora activada. Vas a escuchar avisos suaves al avanzar y guardar.");
    } else {
      setMessage("Guía sonora desactivada.");
    }
  };

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let active = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!active) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await settingsApi.get(projectId);
      const merged = mergeSettings(data.settings);
      setForm(merged);
      const initialProgress = getSiteBuilderProgress(merged, productsEnabled);
      setActiveStep(initialProgress.steps.find((step) => !step.done)?.id || initialProgress.steps[0]?.id || "identity");
      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: merged },
        })
      );
    } catch (err) {
      setError(err.message || "No se pudo cargar la configuración de la landing");
    } finally {
      setLoading(false);
    }
  }, [projectId, productsEnabled]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openSettingsAt = React.useCallback(
    (tabIndex = 0, relatedStepId = "") => {
      setActiveStep(relatedStepId || TAB_STEP_MAP[tabIndex] || "identity");
      setSettingsInitialTab(tabIndex);
      setSettingsOpen(true);
      playTone("click");
    },
    [playTone]
  );

  const openSection = React.useCallback(
    (nextSectionId) => {
      const relatedStep = findStepForSection(nextSectionId, productsEnabled);
      if (relatedStep) setActiveStep(relatedStep.id);
      setSectionId(nextSectionId);
      setSectionOpen(true);
      playTone("click");
    },
    [playTone, productsEnabled]
  );

  const selectStep = React.useCallback(
    (stepId) => {
      setActiveStep(stepId);
      playTone("click");
    },
    [playTone]
  );

  React.useEffect(() => {
    if (loading || !requestedMission) return;
    const requestKey = `${projectId}:${requestedMission}`;
    if (missionHandledRef.current === requestKey) return;
    missionHandledRef.current = requestKey;

    if (requestedMission === "datos") openSettingsAt(3);
    else if (requestedMission === "publicar") openSettingsAt(4);
    else if (requestedMission === "identidad") openSettingsAt(0);
    else if (requestedMission === "portada") openSection("hero");

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("mision");
    setSearchParams(nextParams, { replace: true });
  }, [
    loading,
    openSection,
    openSettingsAt,
    projectId,
    requestedMission,
    searchParams,
    setSearchParams,
  ]);

  const persist = React.useCallback(
    async (nextForm, key = "landing") => {
      try {
        setSavingPath(key);
        setError("");
        const data = await settingsApi.update(projectId, settingsPayload(nextForm, productsEnabled));
        const merged = mergeSettings(data.settings);
        setForm(merged);
        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );
        setMessage("Cambios guardados. El progreso de la web fue actualizado.");
        playTone("success");
        return merged;
      } catch (err) {
        setError(err.message || "No se pudo guardar el cambio");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [playTone, productsEnabled, projectId]
  );

  const uploadAssetFile = React.useCallback(
    async (file, metadata = {}) => {
      const response = await landingAssetsApi.upload(projectId, file, {
        pageKey: "home",
        ...metadata,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      return { ...response.asset, value };
    },
    [projectId]
  );

  const deleteAssetFile = React.useCallback(
    async (path) => {
      if (!isManagedLandingAsset(path)) return { ok: true, removed: false };
      return landingAssetsApi.remove(projectId, path);
    },
    [projectId]
  );

  React.useEffect(() => {
    if (loading || progress.complete || settingsOpen || sectionOpen || !nextIncompleteStep) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setMessage(`Siguiente paso: ${nextIncompleteStep.label}. Abrilo para seguir cerrando tu web.`);
      playTone("reminder", false);
    }, 45000);
    return () => window.clearInterval(timer);
  }, [loading, nextIncompleteStep, playTone, progress.complete, sectionOpen, settingsOpen]);

  React.useEffect(() => {
    if (!loading && progress.complete && !completionAnnouncedRef.current) {
      completionAnnouncedRef.current = true;
      setMessage("¡Círculo cerrado! La landing tiene todos los puntos esenciales completos.");
      playTone("success", false);
    }
    if (!progress.complete) completionAnnouncedRef.current = false;
  }, [loading, playTone, progress.complete]);

  if (effectiveProject && !landingEnabled && !loading) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Landing"
        description="La presencia web básica todavía no está habilitada para este proyecto."
      />
    );
  }

  const publicLabel =
    form.publicStatus === "published"
      ? "Publicada"
      : form.publicStatus === "maintenance"
        ? "Mantenimiento"
        : "Borrador";

  return (
    <Stack spacing={2.2}>
      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" spacing={1.15} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: 2.5,
                bgcolor: "rgba(243,90,58,.10)",
                color: BRAND.orange,
              }}
            >
              <AutoAwesomeRoundedIcon />
            </Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: "-.035em", lineHeight: 1.05 }}>
                Diseñá tu Web
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,.64)", mt: 0.45, maxWidth: 820 }}>
                Completá seis pasos concretos. Cada botón abre solamente lo que necesitás editar y el proceso termina cuando lo esencial llega al 100%.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={publicLabel}
            color={form.publicStatus === "published" ? "success" : "default"}
            sx={{ fontWeight: 900 }}
          />
          <Chip
            icon={saving ? undefined : <CloudDoneRoundedIcon />}
            label={saving ? "Guardando..." : `Versión ${form.revision || 1}`}
            variant="outlined"
            color={saving ? "warning" : "default"}
            sx={{ fontWeight: 850 }}
          />
          <Tooltip title={soundEnabled ? "Desactivar guía sonora" : "Activar guía sonora"}>
            <IconButton
              type="button"
              onClick={toggleSound}
              sx={{ border: "1px solid rgba(25,36,47,.14)", bgcolor: soundEnabled ? "rgba(243,90,58,.08)" : "#fff" }}
            >
              {soundEnabled ? <VolumeUpRoundedIcon sx={{ color: BRAND.orange }} /> : <VolumeOffRoundedIcon />}
            </IconButton>
          </Tooltip>
          <Button
            type="button"
            startIcon={<SettingsRoundedIcon />}
            variant="outlined"
            disabled={loading || saving}
            onClick={() => openSettingsAt(0)}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 950,
              color: BRAND.dark,
              borderColor: "rgba(25,36,47,.20)",
            }}
          >
            Ajustes globales
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Stack spacing={1.2}>
          <Skeleton variant="rounded" height={110} />
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}><Skeleton variant="rounded" height={620} /></Grid>
            <Grid item xs={12} lg={8}><Skeleton variant="rounded" height={620} /></Grid>
          </Grid>
        </Stack>
      ) : null}
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      {!loading ? (
        <>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${progress.complete ? "rgba(22,131,75,.22)" : "rgba(243,90,58,.22)"}`,
              bgcolor: progress.complete ? "rgba(22,131,75,.045)" : "rgba(243,90,58,.045)",
              overflow: "hidden",
              position: "relative",
              ...(progress.complete
                ? {
                    "@keyframes completeLandingGlow": {
                      "0%, 100%": { boxShadow: "0 0 0 0 rgba(22,131,75,.08)" },
                      "50%": { boxShadow: "0 0 0 9px rgba(22,131,75,0)" },
                    },
                    animation: "completeLandingGlow 2.2s ease-in-out 3",
                  }
                : {}),
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.6} alignItems={{ md: "center" }} justifyContent="space-between" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.3} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 2.5,
                    bgcolor: progress.complete ? "rgba(22,131,75,.10)" : "rgba(243,90,58,.10)",
                    color: progress.complete ? "#16834b" : BRAND.orange,
                  }}
                >
                  {progress.complete ? <CheckCircleRoundedIcon /> : <RocketLaunchRoundedIcon />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" sx={{ color: progress.complete ? "#16834b" : BRAND.orange, fontWeight: 950, letterSpacing: .6 }}>
                      {progress.complete ? "LANDING COMPLETA" : "SIGUIENTE OBJETIVO"}
                    </Typography>
                    <Chip size="small" label={`${progress.percentage}% esencial`} sx={{ height: 22, fontWeight: 900 }} />
                  </Stack>
                  <Typography sx={{ mt: 0.25, fontWeight: 950, color: BRAND.dark, lineHeight: 1.2 }}>
                    {progress.complete ? "El proceso está cerrado" : nextIncompleteStep?.label}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, color: "rgba(25,36,47,.58)" }}>
                    {progress.complete
                      ? "Todo lo necesario para una web profesional está completo. Lo restante es opcional."
                      : nextIncompleteStep?.description}
                  </Typography>
                </Box>
              </Stack>

              {progress.complete ? (
                productsEnabled ? (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/dashboard/proyectos/${projectId}/productos?crear=1`)}
                    sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950 }}
                  >
                    Cargar producto o servicio
                  </Button>
                ) : null
              ) : (
                <Button
                  variant="contained"
                  endIcon={<ChevronRightRoundedIcon />}
                  onClick={() => selectStep(nextIncompleteStep.id)}
                  sx={{
                    borderRadius: 999,
                    px: 2.2,
                    bgcolor: BRAND.orange,
                    textTransform: "none",
                    fontWeight: 950,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#dc482c", boxShadow: "none" },
                  }}
                >
                  Continuar ahora
                </Button>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              sx={{
                height: 5,
                bgcolor: "rgba(25,36,47,.06)",
                "& .MuiLinearProgress-bar": { bgcolor: progress.complete ? "#16834b" : BRAND.orange },
              }}
            />
          </Card>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} lg={4}>
              <SiteBuilderGuide
                form={form}
                productsEnabled={productsEnabled}
                activeStep={activeStep}
                onSelectStep={selectStep}
              />
            </Grid>
            <Grid item xs={12} lg={8}>
              <LandingControlCenter
                form={form}
                productsEnabled={productsEnabled}
                activeStep={activeStep}
                onOpenSection={openSection}
                onOpenSettings={openSettingsAt}
                onSelectStep={selectStep}
              />
            </Grid>
          </Grid>
        </>
      ) : null}

      <LandingSectionDialog
        open={sectionOpen}
        activeSection={sectionId}
        form={form}
        productsEnabled={productsEnabled}
        saving={savingPath.startsWith("section:")}
        onClose={() => setSectionOpen(false)}
        onSave={(nextForm) => persist(nextForm, `section:${sectionId}`)}
        onOpenSettings={(tabIndex) => openSettingsAt(tabIndex, activeStep)}
      />

      <LandingSettingsDialog
        open={settingsOpen}
        form={form}
        productsEnabled={productsEnabled}
        saving={savingPath === "settings"}
        initialTab={settingsInitialTab}
        showTrialGuide={!progress.complete}
        onClose={() => setSettingsOpen(false)}
        onSave={(nextForm) => persist(nextForm, "settings")}
        onUploadAsset={uploadAssetFile}
        onDeleteAsset={deleteAssetFile}
      />

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={4200}
        onClose={() => setMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={progress.complete ? "success" : "info"}
          variant="filled"
          onClose={() => setMessage("")}
          sx={{ width: "100%", fontWeight: 800 }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
